import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';

/**
 * WCAG A2 — react-axe no console (somente development).
 *
 * Ativar: REACT_APP_A11Y_AXE=true (ver .env.development.local ou yarn start:a11y)
 *
 * Mutex no axe-core 3.5 (alias no config-overrides). Sem isso, corridas geram
 * "Axe is already running" → Promise reject → overlay → menu trava.
 */
if (
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_A11Y_AXE === 'true'
) {
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  const axeCore = require('axe-core');
  const originalRun = axeCore.run.bind(axeCore);
  let axeRunning = false;

  const emptyResults = {
    violations: [],
    passes: [],
    incomplete: [],
    inapplicable: [],
  };

  /** 3rd-party — não controlamos o markup (VLibras / mailgo / reCAPTCHA). */
  const A11Y_EXCLUDE = [
    ['aside[aria-label="Tradutor VLibras para Libras"]'],
    ['[vw]'],
    ['[vw-access-button]'],
    ['[vw-plugin-wrapper]'],
    ['#mailgo'],
    ['#mailgo-tel'],
    ['iframe[src*="recaptcha"]'],
    ['iframe[title*="reCAPTCHA"]'],
    ['.grecaptcha-badge'],
  ];

  const EXCLUDE_RE = /(mailgo|vlibras|\[vw|recaptcha|grecaptcha)/i;

  const isAlreadyRunning = error =>
    error && /already running/i.test(String(error.message || error));

  function withExcludeContext(contextArg) {
    if (contextArg == null) {
      return { include: [document], exclude: A11Y_EXCLUDE };
    }
    if (typeof contextArg === 'string' || contextArg.nodeType) {
      return { include: [contextArg], exclude: A11Y_EXCLUDE };
    }
    if (typeof contextArg === 'object') {
      return {
        ...contextArg,
        exclude: [...(contextArg.exclude || []), ...A11Y_EXCLUDE],
      };
    }
    return contextArg;
  }

  function filterThirdPartyViolations(results) {
    if (!results || !Array.isArray(results.violations)) {
      return results;
    }
    return {
      ...results,
      violations: results.violations
        .map(v => ({
          ...v,
          nodes: (v.nodes || []).filter(node => {
            const target = (node.target || []).join(' ');
            const html = node.html || '';
            return !EXCLUDE_RE.test(target) && !EXCLUDE_RE.test(html);
          }),
        }))
        .filter(v => v.nodes && v.nodes.length > 0),
    };
  }

  axeCore.run = (...args) => {
    const lastArg = args[args.length - 1];
    const hasCallback = typeof lastArg === 'function';

    // react-axe: axe.run(node, options, callback)
    if (hasCallback) {
      const userCb = lastArg;
      const rest = args.slice(0, -1);

      if (rest.length === 0) {
        rest.push(withExcludeContext(document));
      } else {
        rest[0] = withExcludeContext(rest[0]);
      }

      if (axeRunning) {
        userCb(null, emptyResults);
        return undefined;
      }

      axeRunning = true;
      let ret;
      try {
        ret = originalRun(...rest, (error, results) => {
          axeRunning = false;
          if (isAlreadyRunning(error)) {
            userCb(null, emptyResults);
            return;
          }
          userCb(error, filterThirdPartyViolations(results));
        });
      } catch (error) {
        axeRunning = false;
        userCb(isAlreadyRunning(error) ? null : error, emptyResults);
        return undefined;
      }

      if (ret && typeof ret.then === 'function') {
        ret.catch(() => {
          axeRunning = false;
        });
      }
      return ret;
    }

    if (axeRunning) {
      return Promise.resolve(emptyResults);
    }

    axeRunning = true;
    try {
      const runArgs = [...args];
      if (runArgs.length === 0) {
        runArgs.push(withExcludeContext(document));
      } else if (typeof runArgs[0] !== 'function') {
        runArgs[0] = withExcludeContext(runArgs[0]);
      }
      return Promise.resolve(originalRun(...runArgs))
        .then(filterThirdPartyViolations)
        .catch(() => emptyResults)
        .finally(() => {
          axeRunning = false;
        });
    } catch (error) {
      axeRunning = false;
      return Promise.resolve(emptyResults);
    }
  };

  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  const axe = require('react-axe');
  // Debounce maior: deixa LESS/tema e paint estabilizarem antes do scan (Firefox).
  axe(React, ReactDOM, 4000);
}

ReactDOM.render(<App />, document.getElementById('root'));
